/**
 * Feature Flags - Shared Configuration
 *
 * Centralized feature flag management for StreamSyncEngage SaaS platform.
 * This module is shared between:
 * - Service API (Node.js backend)
 * - Client SaaS (Vite frontend)
 * - Any future microservices
 *
 * Usage:
 *
 * Backend (CommonJS):
 *   const { FEATURE_FLAGS } = require('../../../shared/featureFlags.js');
 *   localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER, data);
 *
 * Frontend (ES Modules):
 *   import { FEATURE_FLAGS } from '../../../shared/featureFlags.js';
 *   localStorage.setItem(FEATURE_FLAGS.FF_SSE_LS_SESSION_USER, data);
 */

/**
 * Feature Flags Registry
 *
 * Naming Convention:
 * - FF_<SCOPE>_<CATEGORY>_<NAME>
 * - SCOPE: SSE (StreamSyncEngage), CORE (Core functionality), UI (User Interface)
 * - CATEGORY: LS (localStorage), API (API), AUTH (Authentication), etc.
 * - NAME: Descriptive feature name in UPPER_SNAKE_CASE
 *
 * @type {Readonly<Object>}
 */
const FEATURE_FLAGS = Object.freeze({
  // ============================================
  // LocalStorage Keys
  // ============================================

  /**
   * Session user entity storage key
   * @type {string}
   * @default "mvv_session_user"
   */
  FF_SSE_LS_SESSION_USER: 'mvv_session_user',

  /**
   * JWT token storage key (managed by mvvLegit)
   * @type {string}
   * @default "mvv"
   * @readonly
   */
  FF_SSE_LS_TOKEN: 'mvv',

  /**
   * Application theme storage key
   * @type {string}
   * @default "mvv_theme"
   */
  FF_SSE_LS_THEME: 'mvv_theme',

  // ============================================
  // UI/UX Configuration
  // ============================================

  /**
   * Dark-mode kill switch. Real consumer: apps/client-saas/src/core/uiFlagGates.js
   * (resolveInitialTheme()/darkModeEnabled()), read by main.js's initTheme()/
   * toggleTheme() and used to hide the side-nav's "theme" footer button when off.
   * When false, the app always renders light regardless of any 'dark' value
   * already persisted in localStorage from before the flag was flipped off.
   *
   * Not coreMeta-backed — flipping this still needs a code deploy, same as every
   * other flag in this file. It exists so an unwanted dark-mode rollout can be
   * pulled with a one-line revert instead of unwinding the feature commit.
   * @type {boolean}
   * @default true
   */
  FF_SSE_UI_DARK_MODE: true,
  // Authoring workspace rollout is independent of the public magnet runtime.
  FF_M5M_MAGNET_EDITOR_WORKSPACE: false,
  // Customer management overview, independent of authoring and the visitor SDK.
  FF_M5M_MAGNET_OVERVIEW: false,

  /**
   * Global animation/transition kill switch. Real consumer:
   * apps/client-saas/src/core/uiFlagGates.js (animationsDisabled()), applied by
   * main.js as a `ff-no-animations` class on <html> and enforced in
   * shared/styles/index.css via a `transition:none!important;animation:none!important`
   * override. Does not require touching the ~20 files with their own
   * transition/@keyframes declarations — the class wins on the cascade.
   * @type {boolean}
   * @default true
   */
  FF_SSE_UI_ANIMATIONS: true,

  // ============================================
  // Client Configuration (LaunchLegitController)
  // ============================================

  /**
   * Auth ping interval in milliseconds
   * How often to check if user's token is still valid
   * @type {number}
   * @default 1440000 (24 minutes)
   */
  FF_CLIENT_AUTH_PING_MS: 1440000,

  /**
   * Carousel element ID
   * @type {string}
   * @default "main_carousel_nav"
   */
  FF_CLIENT_CAROUSEL_ID: 'main_carousel_nav',

  /**
   * LocalStorage key for compressed entity state
   * @type {string}
   * @default "sse_entity"
   */
  FF_CLIENT_LS_SESSION_ENTITY: 'sse_entity',

  /**
   * LocalStorage key for deep-link GUID
   * @type {string}
   * @default "sse_deeplink"
   */
  FF_CLIENT_LS_DEEP_LINK: 'sse_deeplink',

  // ============================================
  // Route Names
  // ============================================

  /**
   * Sign-in route name
   * @type {string}
   * @default "signin_route"
   */
  FF_CLIENT_ROUTE_SIGNIN: 'signin_route',

  /**
   * Sign-up route name
   * @type {string}
   * @default "signup_route"
   */
  FF_CLIENT_ROUTE_SIGNUP: 'signup_route',

  /**
   * Home route name
   * @type {string}
   * @default "home_route"
   */
  FF_CLIENT_ROUTE_HOME: 'home_route',

  /**
   * Reset password (authenticated) route name
   * @type {string}
   * @default "resethash_route"
   */
  FF_CLIENT_ROUTE_RESET_HASH: 'resethash_route',

  /**
   * Reset password (forgot flow) route name
   * @type {string}
   * @default "resetforgot_route"
   */
  FF_CLIENT_ROUTE_RESET_FORGOT: 'resetforgot_route',

  /**
   * Sign-out route name
   * @type {string}
   * @default "signout_route"
   */
  FF_CLIENT_ROUTE_SIGNOUT: 'signout_route',

  /**
   * Error route name
   * @type {string}
   * @default "error_route"
   */
  FF_CLIENT_ROUTE_ERROR: 'error_route',

  /**
   * Email verification prompt route name
   * @type {string}
   * @default "verf_link_route"
   */
  FF_CLIENT_ROUTE_VERF_LINK: 'verf_link_route',

  /**
   * Offline status route name
   * @type {string}
   * @default "offline_route"
   */
  FF_CLIENT_ROUTE_OFFLINE: 'offline_route',

  /**
   * Protected routes (require authentication)
   * @type {Array<string>}
   */
  FF_CLIENT_PROTECTED_ROUTES: [
    'home_route',
    'resethash_route',
    'signout_route',
    'user_management_route'
  ],

  // ============================================
  // API Endpoints
  // ============================================

  /**
   * Sign-in endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/signin"
   */
  FF_CLIENT_ENDPOINT_SIGNIN: '/m5t/v5/acctEntity/signin',

  /**
   * Sign-out endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/signout"
   */
  FF_CLIENT_ENDPOINT_SIGNOUT: '/m5t/v5/acctEntity/signout',

  /**
   * Sign-up endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/"
   */
  FF_CLIENT_ENDPOINT_SIGNUP: '/m5t/v5/acctEntity/',

  /**
   * Reset password (authenticated) endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/resetHash"
   */
  FF_CLIENT_ENDPOINT_RESET_HASH: '/m5t/v5/acctEntity/resetHash',

  /**
   * Reset password (forgot flow) endpoint path
   * @type {string}
   * @default "/m5t/v5/acctEntity/resetforgot"
   */
  FF_CLIENT_ENDPOINT_RESET_FORGOT: '/m5t/v5/acctEntity/resetforgot',

  /**
   * Auth ping endpoint path
   * @type {string}
   * @default "/ping"
   */
  FF_CLIENT_ENDPOINT_PING: '/ping',

  /**
   * Metering endpoint path
   * @type {string}
   * @default "/m5t/v5/coreMeter/"
   */
  FF_CLIENT_ENDPOINT_METER: '/m5t/v5/coreMeter/',

  // ============================================
  // Metering Captions
  // ============================================

  /**
   * Deep-link access metering caption
   * @type {string}
   * @default "deep_link_access"
   */
  FF_CLIENT_METER_DEEP_LINK: 'deep_link_access',

  /**
   * Client-initiated signout metering caption
   * @type {string}
   * @default "signout_client_initiated"
   */
  FF_CLIENT_METER_SIGNOUT_CLIENT: 'signout_client_initiated',

  /**
   * Server-initiated signout metering caption
   * @type {string}
   * @default "signout_server_initiated"
   */
  FF_CLIENT_METER_SIGNOUT_SERVER: 'signout_server_initiated',

  /**
   * Successful auth ping metering caption
   * @type {string}
   * @default "ping_auth_success"
   */
  FF_CLIENT_METER_PING_SUCCESS: 'ping_auth_success',

  /**
   * Failed auth ping metering caption
   * @type {string}
   * @default "ping_auth_failure"
   */
  FF_CLIENT_METER_PING_FAILURE: 'ping_auth_failure',

  // ============================================
  // Internationalization (i18n) Configuration
  // ============================================

  /**
   * Product identifier for microcopy resolution
   * @type {string}
   * @default "SSE"
   * @description StreamSyncEngage product identifier
   */
  FF_SSE_I18N_PRODUCT: 'SSE',

  /**
   * Whitelabel identifier for microcopy resolution
   * @type {string}
   * @default "M5T"
   * @description Whitelabel/brand identifier (M5T, custom client brands, etc.)
   */
  FF_SSE_I18N_WHITELABEL: 'M5T',

  /**
   * Current language code for microcopy resolution
   * @type {string}
   * @default "en"
   * @description ISO language code (en, es, zh-Hans, etc.)
   */
  FF_SSE_I18N_LANG: 'en',

  /**
   * Delay in milliseconds before applying i18n to DOM
   * @type {number}
   * @default 100
   * @description Delay to ensure DOM/Shadow DOM is fully hydrated before applying microcopy
   */
  FF_SSE_I18N_APPLY_DELAY_MS: 100,

  /**
   * Enable i18n debug logging
   * @type {boolean}
   * @default false
   */
  FF_SSE_I18N_DEBUG: false,

  /**
   * LocalStorage key for user language preference
   * @type {string}
   * @default "mvv_user_lang"
   */
  FF_SSE_LS_USER_LANG: 'mvv_user_lang',

  /**
   * LocalStorage key for whitelabel preference
   * @type {string}
   * @default "mvv_whitelabel"
   */
  FF_SSE_LS_WHITELABEL: 'mvv_whitelabel',

  // ============================================
  // API Rate Limiting
  // ============================================
  // Defaults layer for apps/service-api/src/middleware/rateLimits.js. coreMeta
  // rows of the same token override these at runtime (5-min ConfigLoader TTL);
  // env RATE_LIMIT_ENFORCE=false overrides everything as an emergency stop.
  //
  // Every limiter ships OBSERVE-ONLY (*_ENFORCE: false): the check runs, the hit
  // is metered, the request is allowed through. Enable one limiter at a time,
  // starting with SIGNIN, and watch coreMeter between each.
  //
  // *_LIMIT values are re-read per request, so retuning a threshold needs no
  // deploy. Window sizes are NOT runtime-tunable (see the middleware header).

  /** Enforce signin brute-force limits (per-IP + per-email, failures only). */
  // ── RATE LIMITING IS NOW ENFORCED ────────────────────────────────────────────
  // Every one of these shipped as `false`, i.e. observe-only: the limiters counted
  // and logged, and let the request through. Nothing was actually limited. Turned on
  // 2026-08-14 at ZERO CUSTOMERS, deliberately — before any real traffic depends on
  // the current behaviour and while a wrong number costs nothing to correct.
  //
  // The LIMIT values below are unchanged. Only enforcement moved, so the ceilings are
  // the ones already reasoned about at each limiter's definition in
  // middleware/rateLimits.js; see there for why each number is what it is.
  //
  // ConfigLoader resolves coreMeta -> THIS FILE -> the call-site fallback, so a
  // coreMeta row still overrides any of these per environment without a deploy, and
  // EMERGENCY_OFF in rateLimits.js remains the global kill switch.
  FF_API_RATELIMIT_SIGNIN_ENFORCE: true,
  /** Failed signins per IP per 15 min. */
  FF_API_RATELIMIT_SIGNIN_IP_LIMIT: 20,
  /** Failed signins per email per 15 min. */
  FF_API_RATELIMIT_SIGNIN_EMAIL_LIMIT: 10,

  /** Enforce password-reset / signup limits. */
  FF_API_RATELIMIT_FORGOT_ENFORCE: true,
  /** Reset+signup requests per IP per hour. */
  FF_API_RATELIMIT_FORGOT_IP_LIMIT: 15,
  /** Reset+signup requests per email per hour. */
  FF_API_RATELIMIT_FORGOT_EMAIL_LIMIT: 5,
  /** Reset-completion submits per ACCOUNT per hour — bounds reset-token guessing. */
  FF_API_RATELIMIT_RESET_ACCOUNT_LIMIT: 5,

  /** Enforce public lead-capture limits. Revenue path — tune generously. */
  FF_API_RATELIMIT_CAPTURE_ENFORCE: true,
  /** Captures per magnet per IP per hour. */
  FF_API_RATELIMIT_CAPTURE_LIMIT: 30,

  /** Enforce public funnel-beacon limits. */
  FF_API_RATELIMIT_EVENT_ENFORCE: true,
  /** Beacons per magnet per IP per hour. */
  FF_API_RATELIMIT_EVENT_LIMIT: 600,

  /** Enforce the public CSP-block self-report pixel's limits. */
  FF_API_RATELIMIT_CSP_PING_ENFORCE: true,
  /** Pings per magnet per IP per hour. */
  FF_API_RATELIMIT_CSP_PING_LIMIT: 30,

  /** Enforce public passkey-authentication limits (no email to key a second limiter on). */
  FF_API_RATELIMIT_WEBAUTHN_AUTH_ENFORCE: true,
  /** Passkey sign-in attempts per IP per 15 min. */
  FF_API_RATELIMIT_WEBAUTHN_AUTH_IP_LIMIT: 20,

  /**
   * Enforce limits on integrations.js's public surface: Google Ads/GA4/
   * Calendly OAuth callbacks plus the Calendly/CallRail webhook receivers.
   * No appGuid to key on, so IP-only.
   */
  FF_API_RATELIMIT_INTEGRATIONS_ENFORCE: true,
  /** Callback/webhook requests per IP per 15 min. */
  FF_API_RATELIMIT_INTEGRATIONS_LIMIT: 60,

  /**
   * Enforce limits on m5mQuestionnaireResponse's public surface: the
   * client-facing fill-out link (GET definition+draft, POST submit). No
   * appGuid to key on, so IP-only.
   */
  FF_API_RATELIMIT_QUESTIONNAIRE_ENFORCE: true,
  /** Fill-out requests per IP per 15 min. */
  FF_API_RATELIMIT_QUESTIONNAIRE_LIMIT: 30,

  /**
   * Enforce limits on m5mDeliverable's public surface: the client-facing
   * review link (GET plan+review, POST submit, PUT edit). No appGuid to key
   * on, so IP-only — same posture as the questionnaire's.
   */
  FF_API_RATELIMIT_DELIVERABLE_ENFORCE: true,
  /** Review-link requests per IP per 15 min. */
  FF_API_RATELIMIT_DELIVERABLE_LIMIT: 30,

  /**
   * Enforce limits on the public pairing-code exchange (POST /magnet/pair), the
   * one unauthenticated endpoint that issues a credential. IP-keyed; a real site
   * calls it once at setup.
   */
  FF_API_RATELIMIT_PAIRING_ENFORCE: true,
  /** Pairing attempts per IP per 15 min. */
  FF_API_RATELIMIT_PAIRING_LIMIT: 20,

  /**
   * Enforce limits on credential-authenticated site operations (/magnet/site/*).
   * The caller already holds a per-site credential, so this bounds a runaway
   * install rather than a guessing attack. IP-keyed.
   */
  FF_API_RATELIMIT_SITE_OPS_ENFORCE: true,
  /** Site operations per IP per 15 min. */
  FF_API_RATELIMIT_SITE_OPS_LIMIT: 120,

  /** Enforce per-account ceiling on tenancy-scoped routers. */
  FF_API_RATELIMIT_AUTHENTICATED_ENFORCE: true,
  /** Requests per account per 15 min. */
  FF_API_RATELIMIT_AUTHENTICATED_LIMIT: 600,

  /**
   * Require an Origin/Referer (or a valid per-app X-M5M-Key) on the public
   * magnet capture/event endpoints.
   *
   * OFF = observe-only: origin-less requests are still allowed but recorded as
   * magnet_originless_request in coreMeter. Capture is the revenue path, so
   * measure real origin-less traffic before switching this on.
   * Env MAGNET_REQUIRE_ORIGIN=false forces observe-only regardless.
   */
  FF_API_MAGNET_REQUIRE_ORIGIN_ENFORCE: false,

  /** Enforce the coarse per-IP net in front of all routes. */
  FF_API_RATELIMIT_GLOBAL_ENFORCE: true,
  /** Requests per IP per 15 min. */
  FF_API_RATELIMIT_GLOBAL_LIMIT: 2000,
});

/**
 * Get feature flag value with optional override from environment variables
 *
 * @param {string} flagName - Name of the feature flag
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Feature flag value
 *
 * @example
 * const sessionKey = getFlag('FF_SSE_LS_SESSION_USER', 'session_user');
 */
function getFlag(flagName, defaultValue = null) {
  // Check if flag exists in registry
  if (flagName in FEATURE_FLAGS) {
    return FEATURE_FLAGS[flagName];
  }

  // Check environment variables (Node.js)
  if (typeof process !== 'undefined' && process.env && process.env[flagName]) {
    const envValue = process.env[flagName];

    // Parse boolean strings
    if (envValue === 'true') return true;
    if (envValue === 'false') return false;

    // Parse numeric strings
    if (!isNaN(envValue)) return Number(envValue);

    return envValue;
  }

  // Check Vite environment variables (Frontend)
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[flagName]) {
    const envValue = import.meta.env[flagName];

    // Parse boolean strings
    if (envValue === 'true') return true;
    if (envValue === 'false') return false;

    // Parse numeric strings
    if (!isNaN(envValue)) return Number(envValue);

    return envValue;
  }

  return defaultValue;
}

/**
 * Check if feature flag is enabled (boolean flags only)
 *
 * @param {string} flagName - Name of the feature flag
 * @returns {boolean} True if enabled, false otherwise
 *
 * @example
 * if (isEnabled('FF_SSE_UI_DARK_MODE')) {
 *   // Render the dark theme
 * }
 */
function isEnabled(flagName) {
  return getFlag(flagName, false) === true;
}

/**
 * Get all feature flags as a plain object
 *
 * @returns {Object} All feature flags
 */
function getAllFlags() {
  return { ...FEATURE_FLAGS };
}

/**
 * Export for both CommonJS (Node.js) and ES Modules (Frontend)
 */
// ES Modules (default)
export { FEATURE_FLAGS, getFlag, isEnabled, getAllFlags };

// CommonJS (Node.js)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    FEATURE_FLAGS,
    getFlag,
    isEnabled,
    getAllFlags
  };
}
